import { useReservoirBuy } from "@/hooks/reservoir/use-buy";
import { useReservoirBuyPrecheck } from "@/hooks/reservoir/use-buy-precheck";
import { useGetErc20Balance } from "@/hooks/use-get-erc-20-balance";
import { useGetLoggedInUser } from "@/hooks/use-get-logged-in-user";
import { useStringWithPlusMinus } from "@/hooks/useStringWithPlusMinus";
import { usdcContractAddress } from "@talentir/shared/abis/erc-20";
import { SHARE_TO_PERCENT_CONVERSION } from "@talentir/shared/config";
import Big from "big.js";
import { useMemo } from "react";
import { toast } from "sonner";
import type { Asset } from "../order-dialog-v2";
import { OrderCommon } from "./common";

export function Buy({ asset, onComplete, onOnramp }: { asset: Asset; onComplete: () => void; onOnramp: () => void }) {
    const { user } = useGetLoggedInUser();
    const usdcBalance = useGetErc20Balance(usdcContractAddress, "USD", user?.id);

    const [cost, , strippedCost, setRawCost] = useStringWithPlusMinus("-");
    const [percent, , strippedPercent, setRawPercent] = useStringWithPlusMinus("+");

    const { getCostForQuantity, getQuantityUpToCost, availableQuantity } = useReservoirBuyPrecheck(
        asset.blockchainTokenId ?? undefined,
    );
    const { buyAsync, isLoading } = useReservoirBuy(asset.blockchainTokenId ?? undefined);

    const price = useMemo(() => {
        try {
            return Big(strippedCost).div(strippedPercent).toNumber();
        } catch {
            return 0;
        }
    }, [strippedCost, strippedPercent]);

    function onCostChange(cost: string) {
        try {
            setRawCost(cost);

            const costBig = BigInt(Big(cost).mul(Big(10).pow(6)).round().toString());
            const quantity = getQuantityUpToCost(costBig);

            if (quantity == null) {
                setRawPercent("");
                return;
            }

            const percent = Big(quantity?.toString()).div(Big(SHARE_TO_PERCENT_CONVERSION));
            setRawPercent(percent.toString());
        } catch {
            setRawPercent("");
        }
    }

    function onPercentChange(percent: string) {
        try {
            setRawPercent(percent);

            const percentBig = BigInt(Big(percent).mul(Big(SHARE_TO_PERCENT_CONVERSION)).round().toString());
            const cost = getCostForQuantity(percentBig);

            if (cost == null) {
                setRawCost("");
                return;
            }

            setRawCost(cost.totalCost.toString());
        } catch {
            setRawCost("");
        }
    }

    async function buy() {
        try {
            let costRaw: bigint | undefined;
            let quantityRaw: bigint | undefined;
            try {
                costRaw = BigInt(Big(strippedCost).mul(Big(10).pow(6)).round().toString());
            } catch {
                toast.error("Error", { description: "Please enter a valid price" });
                return;
            }

            try {
                quantityRaw = BigInt(Big(strippedPercent).mul(Big(SHARE_TO_PERCENT_CONVERSION)).round().toString());
            } catch {
                toast.error("Error", { description: "Please enter a valid amount" });
                return;
            }

            await buyAsync(quantityRaw, costRaw);
            onComplete();
        } catch {}
    }

    const quantityError = useMemo(() => {
        try {
            if (
                (strippedCost !== "" && strippedPercent === "") ||
                Big(strippedPercent).gt(Big(availableQuantity.percent))
            ) {
                return "Not that much available for purchase 😢";
            }
        } catch {}
    }, [strippedPercent, availableQuantity.percent, strippedCost]);

    const costError = useMemo(() => {
        try {
            if (Big(strippedCost).gt(Big(usdcBalance?.balanceString ?? "0"))) {
                return "Your wallet balance is too low 😢";
            }
        } catch {}
    }, [strippedCost, usdcBalance?.balanceString]);

    return (
        <>
            <OrderCommon
                asset={asset}
                cta="Buy"
                buttonLabel={costError == null ? "Buy" : "Top Up"}
                useConfirmDialog={costError == null}
                topCurrency="$"
                topLeftInfo={`Current Balance: ${usdcBalance?.balanceFormatted ?? "$0.00"}`}
                topLabel="You Pay USD"
                topValue={cost}
                onTopValueChange={onCostChange}
                topDecimals={2}
                bottomCurrency="%"
                bottomLeftInfo={`Available for purchase: ${availableQuantity.percentString}`}
                bottomLabel="You get %"
                bottomValue={percent}
                onBottomValueChange={onPercentChange}
                bottomDecimals={4}
                bottomError={quantityError}
                priceForOnePercent={price.toLocaleString("en-US", { style: "currency", currency: "USD" })}
                onComplete={() => {
                    if (costError != null) {
                        onOnramp();
                    } else {
                        void buy();
                    }
                }}
                isLoading={isLoading}
            />
        </>
    );
}
